import { useState } from 'react';
import axios from 'axios';
import { X, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal para marcar manualmente una orden como paid.
 * Soporta órdenes en cualquier status non-paid (pending/expired/failed).
 * Ideal para reconciliar pagos huérfanos donde el dinero entró al banco
 * pero la orden expiró o falló antes de la conciliación automática.
 */
export default function MarkOrderPaidModal({ order, onClose, onSuccess, setToast }) {
  const [paymentReference, setPaymentReference] = useState(order.payment_reference || '');
  const [amount, setAmount] = useState(order.amount || '');
  const [paymentMethod, setPaymentMethod] = useState(order.payment_method_used || 'p2p');
  const [paidAtDate, setPaidAtDate] = useState(new Date().toISOString().slice(0, 10));
  const [paidAtTime, setPaidAtTime] = useState('12:00');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!paymentReference.trim()) { setError('La referencia bancaria es obligatoria.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('El monto debe ser mayor a 0.'); return; }

    const paidAtIso = new Date(`${paidAtDate}T${paidAtTime}:00Z`).toISOString();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/orders/${order.order_code}/mark-paid`,
        {
          payment_reference: paymentReference.trim(),
          amount: parseFloat(amount),
          payment_method: paymentMethod,
          paid_at: paidAtIso,
          notes: notes.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast?.({ type: 'success', message: `Orden ${order.order_code} marcada como paid` });
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al marcar como paid');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="mark-paid-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#003594' }}>
          <div className="flex items-center gap-3 text-white">
            <CheckCircle2 className="w-5 h-5" />
            <div>
              <h3 className="text-lg font-bold">Marcar orden como paid</h3>
              <p className="text-xs text-blue-200 font-mono">
                {order.order_code} · {order.currency} {Number(order.amount).toFixed(2)} · status actual: <span className="uppercase">{order.status}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300" data-testid="mark-paid-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Esta acción marcará la orden como paid saltándose la verificación bancaria automática. El monto se acreditará al ledger del comercio.
              Usar solo cuando tengas certeza del pago en el banco.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Referencia bancaria *</label>
            <input
              type="text" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Ej: 863432482" data-testid="mark-paid-reference"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Monto pagado (VES) *</label>
              <input
                type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="mark-paid-amount"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Método</label>
              <select
                value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="mark-paid-method"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="p2p">P2P</option>
                <option value="c2p">C2P</option>
                <option value="immediate_debit">Débito Inmediato</option>
                <option value="bcv_transfer">Transferencia BCV</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Fecha del pago</label>
              <input
                type="date" value={paidAtDate} onChange={(e) => setPaidAtDate(e.target.value)}
                data-testid="mark-paid-date"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Hora (UTC)</label>
              <input
                type="time" value={paidAtTime} onChange={(e) => setPaidAtTime(e.target.value)}
                data-testid="mark-paid-time"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Notas (opcional)</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Ej: Reconciliación manual — payer confirmó pago por WhatsApp" data-testid="mark-paid-notes"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2" data-testid="mark-paid-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-50" data-testid="mark-paid-cancel">
              Cancelar
            </button>
            <button
              type="submit" disabled={submitting} data-testid="mark-paid-submit"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</> : <><CheckCircle2 className="w-4 h-4" /> Marcar como paid</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
